import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, ChannelPill, Icon, I, Pill, TEXT_WRAP_STYLE, mobileButtonStyle } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { listOrderFulfillmentRules, saveOrderFulfillmentRule } from '../orderFulfillment/orderFulfillmentApi'
import type { ChannelOrderFulfillmentLocation, ChannelOrderFulfillmentRule } from '../orderFulfillment/types'
import { listWarehouses } from '../warehouses/warehousesApi'
import type { Warehouse } from '../warehouses/types'

interface TlaaqInventoryDetailPageProps {
  accessToken: string
  onBack: () => void
  onManageWarehouses: () => void
  onOpenStockAllocation: () => void
}

function providerName(provider: ChannelOrderFulfillmentRule['provider']): string {
  return provider === 'ZID' ? 'Zid' : provider === 'SALLA' ? 'Salla' : provider === 'NOON' ? 'Noon' : provider === 'AMAZON' ? 'Amazon' : 'Trendyol'
}

function supported(rule: ChannelOrderFulfillmentRule): boolean {
  return rule.provider !== 'TRENDYOL'
}

export function TlaaqInventoryDetailPage({ accessToken, onBack, onManageWarehouses, onOpenStockAllocation }: TlaaqInventoryDetailPageProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [rules, setRules] = useState<ChannelOrderFulfillmentRule[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [editing, setEditing] = useState<ChannelOrderFulfillmentRule | null>(null)
  const [defaultWarehouseId, setDefaultWarehouseId] = useState('')
  const [locationWarehouseIds, setLocationWarehouseIds] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)

  useEffect(() => {
    let cancelled = false
    Promise.all([listOrderFulfillmentRules(accessToken), listWarehouses(accessToken)])
      .then(([nextRules, nextWarehouses]) => {
        if (cancelled) return
        setRules(nextRules)
        setWarehouses(nextWarehouses)
        setError(false)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [accessToken])

  const needsAttention = rules.filter(rule => supported(rule) && rule.defaultWarehouseId == null).length
  const warehouseById = useMemo(() => new Map(warehouses.map(warehouse => [warehouse.id, warehouse])), [warehouses])

  function openEditor(rule: ChannelOrderFulfillmentRule) {
    setEditing(rule)
    setDefaultWarehouseId(rule.defaultWarehouseId == null ? '' : String(rule.defaultWarehouseId))
    setLocationWarehouseIds(Object.fromEntries(rule.locations.map(location => [location.externalLocationId, String(location.warehouseId)])))
    setSaveError(false)
  }

  function allLocations(rule: ChannelOrderFulfillmentRule): string[] {
    return Array.from(new Set([
      ...rule.locations.map(location => location.externalLocationId),
      ...rule.observedLocationIds,
    ]))
  }

  async function save() {
    if (!editing || !defaultWarehouseId) return
    const locations: ChannelOrderFulfillmentLocation[] = allLocations(editing)
      .filter(location => Boolean(locationWarehouseIds[location]))
      .map(location => ({ externalLocationId: location, warehouseId: Number(locationWarehouseIds[location]) }))
    setSaving(true)
    setSaveError(false)
    try {
      const updated = await saveOrderFulfillmentRule(accessToken, editing.channelConnectionId, {
        defaultWarehouseId: Number(defaultWarehouseId),
        locations,
      })
      setRules(current => current.map(rule => rule.channelConnectionId === updated.channelConnectionId ? updated : rule))
      setEditing(null)
    } catch {
      setSaveError(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <button type="button" onClick={onBack} style={{ border: 0, background: 'transparent', padding: 0, color: 'var(--fg-muted)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 }}>
        {t('pages.tlaaqInventory.back')}
      </button>

      <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', marginTop: 14, marginBottom: 20 }}>
        <div style={TEXT_WRAP_STYLE}>
          <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 22 }}>{t('pages.tlaaqInventory.title')}</h2>
          <p style={{ margin: '5px 0 0', color: 'var(--fg-muted)', fontSize: 13.5 }}>{t('pages.tlaaqInventory.description')}</p>
        </div>
        <Button kind="secondary" size="sm" icon={I.warehouse} onClick={onManageWarehouses} style={mobileButtonStyle(isMobile)}>{t('pages.tlaaqInventory.manageWarehouses')}</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))', gap: 14, marginBottom: 20 }}>
        <Card padding={18}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Icon size={18} color="var(--brand-700)">{I.layers}</Icon><strong>{t('pages.tlaaqInventory.allocation.title')}</strong></div>
          <p style={{ color: 'var(--fg-muted)', fontSize: 12.5, lineHeight: 1.55 }}>{t('pages.tlaaqInventory.allocation.body')}</p>
          <Button kind="secondary" size="sm" onClick={onOpenStockAllocation}>{t('pages.tlaaqInventory.allocation.action')}</Button>
        </Card>
        <Card padding={18} style={{ borderColor: needsAttention > 0 ? 'var(--danger-100)' : 'var(--brand-200)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}><Icon size={18} color="var(--brand-700)">{I.warehouse}</Icon><strong>{t('pages.tlaaqInventory.deduction.title')}</strong>{needsAttention > 0 && <Pill tone="danger" size="sm">{t('pages.tlaaqInventory.deduction.needsAttention', { count: needsAttention })}</Pill>}</div>
          <p style={{ color: 'var(--fg-muted)', fontSize: 12.5, lineHeight: 1.55 }}>{t('pages.tlaaqInventory.deduction.body')}</p>
        </Card>
      </div>

      {loading && <Card padding={20}>{t('pages.tlaaqInventory.loading')}</Card>}
      {error && <Card padding={20} style={{ borderColor: 'var(--danger-100)', color: 'var(--danger)' }}>{t('pages.tlaaqInventory.loadError')}</Card>}

      {!loading && !error && (
        <Card padding={0}>
          <div style={{ padding: 18, borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
            <div><strong>{t('pages.tlaaqInventory.mappings.title')}</strong><div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 3 }}>{t('pages.tlaaqInventory.mappings.body')}</div></div>
          </div>
          <div style={{ display: 'grid' }}>
            {rules.map(rule => {
              const defaultWarehouse = rule.defaultWarehouseId == null ? null : warehouseById.get(rule.defaultWarehouseId)
              const isSupported = supported(rule)
              return (
                <div key={rule.channelConnectionId} style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(180px, 1fr) minmax(180px, 1fr) minmax(190px, 1fr) auto', gap: 12, alignItems: 'center', padding: 16, borderTop: '1px solid var(--border-subtle)', background: isSupported && !defaultWarehouse ? 'var(--danger-100)' : 'white' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}><ChannelPill name={providerName(rule.provider)} size="sm" /><span style={{ fontWeight: 700, ...TEXT_WRAP_STYLE }}>{rule.channelDisplayName}</span></div>
                  <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>{rule.locations.length > 0 ? t('pages.tlaaqInventory.mappings.locationCount', { count: rule.locations.length }) : t('pages.tlaaqInventory.mappings.defaultOnly')}</div>
                  <div>
                    {defaultWarehouse ? <><strong style={{ fontSize: 13 }}>{defaultWarehouse.name}</strong><div style={{ color: 'var(--fg-subtle)', fontSize: 11.5 }}>{t('pages.tlaaqInventory.mappings.default')}</div></> : isSupported ? <><strong style={{ color: 'var(--danger)', fontSize: 13 }}>{t('pages.tlaaqInventory.mappings.notConfigured')}</strong><div style={{ color: 'var(--danger)', fontSize: 11.5 }}>{t('pages.tlaaqInventory.mappings.noDeduction')}</div></> : <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>{t('pages.tlaaqInventory.mappings.providerUnknown')}</span>}
                  </div>
                  <Button kind={defaultWarehouse ? 'secondary' : 'primary'} size="sm" onClick={() => openEditor(rule)} disabled={!isSupported} style={mobileButtonStyle(isMobile)}>{defaultWarehouse ? t('pages.tlaaqInventory.mappings.edit') : t('pages.tlaaqInventory.mappings.configure')}</Button>
                </div>
              )
            })}
            {rules.length === 0 && <div style={{ padding: 28, color: 'var(--fg-muted)', textAlign: 'center' }}>{t('pages.tlaaqInventory.mappings.empty')}</div>}
          </div>
        </Card>
      )}

      {editing && (
        <div role="dialog" aria-modal="true" aria-label={String(t('pages.tlaaqInventory.editor.title'))} style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(15,23,42,0.32)', display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: isMobile ? '100%' : 470, maxWidth: '100%', height: '100%', background: 'white', padding: isMobile ? 18 : 24, overflowY: 'auto', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
              <div><h3 style={{ margin: 0, color: 'var(--brand-900)' }}>{t('pages.tlaaqInventory.editor.title')}</h3><div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 4 }}>{editing.channelDisplayName}</div></div>
              <button type="button" aria-label={String(t('pages.tlaaqInventory.editor.close'))} onClick={() => setEditing(null)} style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--fg-muted)' }}><Icon size={18}>{I.x}</Icon></button>
            </div>

            <div>
              <label style={{ fontWeight: 700, fontSize: 13 }}>{t('pages.tlaaqInventory.editor.defaultLabel')}</label>
              <p style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.5 }}>{t('pages.tlaaqInventory.editor.defaultHelp')}</p>
              <select aria-label={String(t('pages.tlaaqInventory.editor.defaultLabel'))} value={defaultWarehouseId} onChange={event => setDefaultWarehouseId(event.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${defaultWarehouseId ? 'var(--border-default)' : 'var(--danger)'}`, background: 'white', color: 'var(--fg)', fontFamily: 'inherit' }}>
                <option value="">{t('pages.tlaaqInventory.editor.chooseWarehouse')}</option>
                {warehouses.map(warehouse => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
              </select>
              {!defaultWarehouseId && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 5 }}>{t('pages.tlaaqInventory.editor.defaultRequired')}</div>}
            </div>

            {allLocations(editing).length > 0 && <div style={{ display: 'grid', gap: 12 }}>
              <strong style={{ fontSize: 13 }}>{t('pages.tlaaqInventory.editor.locations')}</strong>
              {allLocations(editing).map(location => <label key={location} style={{ display: 'grid', gap: 6 }}><span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{location}</span><select value={locationWarehouseIds[location] ?? ''} onChange={event => setLocationWarehouseIds(current => ({ ...current, [location]: event.target.value }))} style={{ width: '100%', padding: '9px 11px', borderRadius: 10, border: '1px solid var(--border-default)', background: 'white', fontFamily: 'inherit' }}><option value="">{t('pages.tlaaqInventory.editor.useDefault')}</option>{warehouses.map(warehouse => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}</select></label>)}
            </div>}

            <Card padding={14} style={{ background: 'var(--warning-100)', borderColor: 'var(--warning-100)', fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.5 }}>{t('pages.tlaaqInventory.editor.providerFulfilled')}</Card>
            <Button kind="ghost" size="sm" onClick={onManageWarehouses}>{t('pages.tlaaqInventory.manageWarehouses')}</Button>
            {saveError && <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{t('pages.tlaaqInventory.editor.saveError')}</div>}
            <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 8 }}><Button kind="secondary" size="sm" onClick={() => setEditing(null)}>{t('pages.tlaaqInventory.editor.cancel')}</Button><Button kind="primary" size="sm" onClick={() => void save()} disabled={!defaultWarehouseId || saving}>{saving ? t('pages.tlaaqInventory.editor.saving') : t('pages.tlaaqInventory.editor.save')}</Button></div>
          </div>
        </div>
      )}
    </div>
  )
}
